/**
 * Local-service · agent-config cards
 *
 * All C-tier (agent-local.json) configuration UI lives here, rendered
 * by [web/src/pages/LocalService.tsx]. Previously these cards lived in
 * the Settings page mixed with B-tier (跟随账号) preferences; per the
 * SPA/ADH split rule (.cursor/rules/spa-and-adh-split.mdc) all
 * agent-bound config now belongs to the 本机服务 page.
 *
 * Each card is independent — they share the local `Card` skeleton and
 * the `useAgentConfig*` hooks but otherwise stand alone. The cards
 * write through `/api/agent/config` (debounced) or `/api/host-tools`
 * and take effect without restarting the agent process (except where
 * `RESTART_REQUIRED_FIELDS` says otherwise).
 *
 * Offline behaviour: when adh is not running, `useAgentConfigStatus().ready`
 * is false and individual queries fail. We render a single banner at
 * the top of the section in LocalService.tsx; the cards themselves
 * render their normal skeleton states from their existing isLoading
 * branches.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  FolderSearch,
  HardDriveDownload,
  Info,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  Sun,
  Terminal,
  Wifi,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import {
  hostToolsApi,
  type HostToolName,
  type HostToolStatus,
  type HostToolsResponse,
  type HostToolsShellSnippets,
  type HostToolResolveSource,
} from '@/lib/api';
import {
  useAgentConfig,
  useAgentConfigStore,
  RESTART_REQUIRED_FIELDS,
} from '@/lib/agentConfig';
import { DeviceFilesDirSetting } from '@/components/DeviceFilesDirSetting';

const CATEGORY_LABEL: Record<string, string> = {
  'android-sdk': 'Android SDK',
  decompile: '反编译',
  binutils: 'Binutils',
  media: '媒体',
};

const CATEGORY_ORDER: string[] = ['android-sdk', 'decompile', 'binutils', 'media'];

const SOURCE_LABEL: Record<HostToolResolveSource, string> = {
  config: '配置文件',
  env: '环境变量',
  sdk: 'Android SDK',
  path: '$PATH',
  none: '未找到',
};

const SOURCE_TONE: Record<HostToolResolveSource, string> = {
  config: 'border-accent/40 bg-accent/10 text-accent',
  env: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  sdk: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  path: 'border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  none: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

/* ----------------------------------------------------------------- */
/* Shared card skeleton                                              */
/* ----------------------------------------------------------------- */

function Card({
  title,
  icon,
  subtitle,
  children,
  footer,
}: {
  title: string;
  icon?: JSX.Element;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </h2>
        {subtitle && (
          <span className="hidden text-[10px] uppercase tracking-wide text-ink-dim md:inline">
            {subtitle}
          </span>
        )}
      </header>
      <div className="space-y-3 px-3 py-3 text-sm">{children}</div>
      {footer && (
        <footer className="border-t border-line px-3 py-2 text-[11px] text-ink-muted">
          {footer}
        </footer>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* Android SDK root                                                  */
/* ----------------------------------------------------------------- */

export function SdkRootCard(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['host-tools'],
    queryFn: () => hostToolsApi.get(),
    refetchOnWindowFocus: false,
  });
  return (
    <Card title="Android SDK" icon={<HardDriveDownload className="h-4 w-4 text-accent" />}>
      {error ? (
        <div className="text-sm text-rose-500">
          <AlertTriangle className="mr-1 inline h-4 w-4" /> 加载失败：{(error as Error).message}
        </div>
      ) : !data || isLoading ? (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> 正在探测工具…
        </div>
      ) : (
        <SdkRootBody data={data} qc={qc} />
      )}
    </Card>
  );
}

function SdkRootBody({
  data,
  qc,
}: {
  data: HostToolsResponse;
  qc: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  const [value, setValue] = useState(data.androidSdkRoot ?? '');
  useEffect(() => {
    setValue(data.androidSdkRoot ?? '');
  }, [data.androidSdkRoot]);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const save = useMutation({
    mutationFn: (next: string | null) => hostToolsApi.save({ androidSdkRoot: next }),
    onSuccess: (resp) => {
      qc.setQueryData(['host-tools'], resp);
      if (resp.persisted) {
        setToast({ kind: 'ok', msg: `已保存到 ${resp.configFile ?? 'agent-local.json'}` });
      } else {
        setToast({ kind: 'err', msg: `运行时已切换，但写入失败：${resp.persistError ?? '未知错误'}` });
      }
      window.setTimeout(() => setToast(null), 4_000);
    },
    onError: (err: Error) => {
      setToast({ kind: 'err', msg: err.message });
      window.setTimeout(() => setToast(null), 4_000);
    },
  });
  const dirty = (value || '') !== (data.androidSdkRoot ?? '');
  return (
    <>
      <div>
        <label className="mb-1 block text-xs text-ink-muted">SDK 根目录路径（支持 ~/，须为绝对路径）</label>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono text-xs"
            placeholder="/Users/<you>/Library/Android/sdk"
            value={value}
            spellCheck={false}
            onChange={(e) => setValue(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            disabled={save.isPending || !dirty}
            onClick={() => save.mutate(value.trim().length > 0 ? value.trim() : null)}
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} 保存
          </button>
          {data.androidSdkRoot && (
            <button
              type="button"
              className="btn-ghost"
              disabled={save.isPending}
              title="清除 SDK 根目录配置（回退到 ANDROID_HOME / 默认路径）"
              onClick={() => save.mutate(null)}
            >
              清除
            </button>
          )}
        </div>
      </div>
      {data.sdkAutodetect.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-1 text-[11px] text-ink-muted">
            <Sparkles className="h-3 w-3" /> 自动探测候选（点击「采用」一键填入）
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.sdkAutodetect.map((cand) => (
              <button
                key={cand.path}
                type="button"
                className={clsx(
                  'group flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition',
                  cand.exists
                    ? 'border-line bg-bg-soft text-ink hover:border-accent/60 hover:bg-accent-soft'
                    : 'border-rose-500/30 bg-rose-500/5 text-rose-700 line-through dark:text-rose-300',
                  cand.configured && 'border-accent/60 bg-accent/10 text-accent',
                )}
                disabled={!cand.exists || save.isPending}
                onClick={() => save.mutate(cand.path)}
                title={cand.exists ? '采用此目录' : '该目录不存在'}
              >
                <FolderSearch className="h-3 w-3 shrink-0" />
                <span className="font-mono">{cand.path}</span>
                {cand.configured && <CheckCircle2 className="h-3 w-3" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {toast && (
        <p className={clsx('text-[11px]', toast.kind === 'ok' ? 'text-emerald-600' : 'text-rose-500')}>{toast.msg}</p>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- */
/* Host tools table                                                  */
/* ----------------------------------------------------------------- */

export function ToolsTableCard(): JSX.Element {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ['host-tools'],
    queryFn: () => hostToolsApi.get(),
    refetchOnWindowFocus: false,
  });
  const rescan = useMutation({
    mutationFn: () => hostToolsApi.rescan(),
    onSuccess: (next) => qc.setQueryData(['host-tools'], next),
  });
  if (!data) return <></>;
  return (
    <Card
      title="工具路径"
      icon={<Terminal className="h-4 w-4 text-accent" />}
      footer="路径写入 agent-local.json；改完立即生效，无需重启 hub。"
    >
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-ghost btn-xs text-xs"
          disabled={rescan.isPending || isFetching}
          onClick={() => rescan.mutate()}
        >
          {rescan.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} 重新扫描
        </button>
      </div>
      <div className="space-y-4">
        <ToolsTableBody data={data} />
      </div>
    </Card>
  );
}

function ToolsTableBody({ data }: { data: HostToolsResponse }): JSX.Element {
  const grouped = useMemo(() => {
    const map = new Map<string, HostToolStatus[]>();
    for (const t of data.tools) {
      const list = map.get(t.category) ?? [];
      list.push(t);
      map.set(t.category, list);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({ category: c, tools: map.get(c)! }));
  }, [data.tools]);
  return (
    <>
      {grouped.map((group) => (
        <div key={group.category}>
          <h3 className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-dim">
            {CATEGORY_LABEL[group.category] ?? group.category}
          </h3>
          <ul className="divide-y divide-line rounded-md border border-line bg-bg-card">
            {group.tools.map((tool) => (
              <ToolRow key={tool.name} tool={tool} />
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function ToolRow({ tool }: { tool: HostToolStatus }): JSX.Element {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [override, setOverride] = useState(tool.configuredFrom === 'config' ? tool.configuredPath ?? '' : '');
  useEffect(() => {
    setOverride(tool.configuredFrom === 'config' ? tool.configuredPath ?? '' : '');
  }, [tool.configuredFrom, tool.configuredPath]);
  const save = useMutation({
    mutationFn: (next: string | null) => hostToolsApi.save({ paths: { [tool.name]: next } }),
    onSuccess: (resp) => {
      qc.setQueryData(['host-tools'], resp);
      setExpanded(false);
    },
  });
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="mt-0.5 text-ink-muted hover:text-ink"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'collapse' : 'expand'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12.5px]">{tool.label}</span>
            {tool.available ? (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" /> 可用
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-rose-600 dark:text-rose-400">
                <XCircle className="h-3 w-3" /> 缺失
              </span>
            )}
            <span
              className={clsx(
                'inline-flex items-center rounded-full border px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wider',
                SOURCE_TONE[tool.source],
              )}
              title={`来源：${SOURCE_LABEL[tool.source]}`}
            >
              {SOURCE_LABEL[tool.source]}
            </span>
            {tool.version && (
              <span className="badge font-mono text-[10px]">v{tool.version.replace(/^v/i, '').slice(0, 24)}</span>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-ink-muted">{tool.description}</p>
          {tool.resolvedPath && (
            <p className="mt-0.5 break-all font-mono text-[10.5px] text-ink-muted">{tool.resolvedPath}</p>
          )}
        </div>
      </div>
      {expanded && (
        <div className="ml-5 mt-2 space-y-2 border-l border-line pl-3 text-xs">
          <div>
            <label className="mb-1 block text-[11px] text-ink-muted">手动覆盖路径（绝对路径，留空恢复自动）</label>
            <div className="flex gap-1.5">
              <input
                className="input flex-1 font-mono text-[11px]"
                value={override}
                placeholder={`例: ${defaultPlaceholderFor(tool.name)}`}
                spellCheck={false}
                onChange={(e) => setOverride(e.target.value)}
              />
              <button
                type="button"
                className="btn"
                disabled={save.isPending}
                onClick={() => save.mutate(override.trim().length > 0 ? override.trim() : null)}
              >
                {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} 保存
              </button>
              {tool.configuredFrom === 'config' && (
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={save.isPending}
                  title="清除手动覆盖，回到自动解析"
                  onClick={() => {
                    setOverride('');
                    save.mutate(null);
                  }}
                >
                  重置
                </button>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10.5px] text-ink-dim">
              支持的环境变量：
              {tool.envNames.map((n, i) => (
                <code key={n} className="ml-1 font-mono">
                  {n}
                  {i < tool.envNames.length - 1 ? ',' : ''}
                </code>
              ))}
            </p>
            {tool.error && (
              <p className="mt-1 text-[10.5px] text-rose-600 dark:text-rose-400">探测错误：{tool.error}</p>
            )}
            {tool.triedPaths.length > 0 && (
              <details className="mt-1 text-[10.5px] text-ink-dim">
                <summary className="cursor-pointer select-none">已查找过的路径（{tool.triedPaths.length}）</summary>
                <ul className="mt-1 max-h-32 overflow-y-auto rounded-sm bg-bg-soft p-1 font-mono">
                  {tool.triedPaths.map((p) => (
                    <li key={p} className="break-all">
                      {p}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function defaultPlaceholderFor(name: HostToolName): string {
  switch (name) {
    case 'apksigner':
    case 'aapt2':
    case 'aapt':
      return '~/Library/Android/sdk/build-tools/35.0.0/' + name;
    case 'apkanalyzer':
      return '~/Library/Android/sdk/cmdline-tools/latest/bin/apkanalyzer';
    case 'bundletool':
      return '/opt/homebrew/bin/bundletool';
    case 'jadx':
      return '/opt/homebrew/bin/jadx-cli';
    case 'apktool':
      return '/opt/homebrew/bin/apktool';
    case 'ffprobe':
      return '/opt/homebrew/bin/ffprobe';
    default:
      return '/usr/local/bin/' + name;
  }
}

/* ----------------------------------------------------------------- */
/* devices.dir                                                       */
/* ----------------------------------------------------------------- */

export function DeviceFilesCard(): JSX.Element {
  return (
    <Card
      title="文件存放目录"
      icon={<Sun className="h-4 w-4 text-accent" />}
      footer="写入 agent-local.json 后立刻生效。"
    >
      <p className="text-xs text-ink-muted">从手机拉取的 APK、截图、录屏、日志都会落到此目录；留空使用 hub 默认 (`~/AndroidDeviceHub/devices`)。</p>
      <div className="flex">
        <DeviceFilesDirSetting />
      </div>
    </Card>
  );
}

/* ----------------------------------------------------------------- */
/* Wireless (wireless.autoProvision / tcpipMirror.enabled)            */
/* ----------------------------------------------------------------- */

export function WirelessAgentCard(): JSX.Element {
  const auto = useAgentConfig((c) => c.wireless.autoProvision);
  const tcpipEnabled = useAgentConfig((c) => c.tcpipMirror.enabled);
  const update = useAgentConfigStore((s) => s.update);
  return (
    <Card title="无线调试" icon={<Wifi className="h-4 w-4 text-accent" />}>
      <label className="flex items-start gap-2 text-xs">
        <input
          type="checkbox"
          checked={auto}
          onChange={(e) => update({ wireless: { autoProvision: e.target.checked } })}
        />
        <span>
          <span className="text-ink">USB 插入手机时自动激活无线 ADB</span>
          <span className="ml-2 text-[10.5px] text-ink-dim">
            首次插一台机会自动 `adb tcpip 5555` 并开新连接，方便随后拔掉 USB 也能继续用。
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-xs">
        <input
          type="checkbox"
          checked={tcpipEnabled}
          onChange={(e) => update({ tcpipMirror: { enabled: e.target.checked } })}
        />
        <span>
          <span className="text-ink">允许 TCP/IP 镜像通道</span>
          <span className="ml-2 text-[10.5px] text-ink-dim">
            关掉后，scrcpy 镜像只走 USB / wireless adb，不再尝试 host-tcp 直连。
          </span>
        </span>
      </label>
    </Card>
  );
}

/* ----------------------------------------------------------------- */
/* Mirror timeouts (advanced collapsible)                            */
/* ----------------------------------------------------------------- */

export function MirrorTimeoutCard(): JSX.Element {
  const [open, setOpen] = useState(false);
  const startup = useAgentConfig((c) => c.mirror.startupTimeoutMs);
  const accept = useAgentConfig((c) => c.mirror.socketAcceptTimeoutMs);
  const update = useAgentConfigStore((s) => s.update);
  return (
    <Card title="镜像协议超时（高级）">
      <button
        type="button"
        className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {open ? '收起' : '展开高级超时设置'}
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumberField
            label="scrcpy server 启动超时 (ms)"
            value={startup}
            min={2000}
            max={120_000}
            onChange={(v) => update({ mirror: { startupTimeoutMs: v } })}
          />
          <NumberField
            label="socket accept 超时 (ms)"
            value={accept}
            min={2000}
            max={60_000}
            onChange={(v) => update({ mirror: { socketAcceptTimeoutMs: v } })}
          />
        </div>
      )}
    </Card>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-muted">{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (Number.isFinite(n) && n >= min && n <= max) onChange(n);
        }}
      />
    </label>
  );
}

/* ----------------------------------------------------------------- */
/* Agent endpoints (read-only + restart-hint)                         */
/* ----------------------------------------------------------------- */

export function AgentEndpointsCard(): JSX.Element {
  const web = useAgentConfig((c) => c.web);
  const devServer = useAgentConfig((c) => c.devServer);
  const adb = useAgentConfig((c) => c.adb);
  return (
    <Card
      title="本机端点 / ADB"
      footer={
        <>
          以下字段需要重启 hub 进程才会真正生效：
          <code className="ml-1 font-mono">{RESTART_REQUIRED_FIELDS.join(', ')}</code>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <ReadOnlyField label="hub web 监听" value={`${web.host}:${web.port}`} />
        <ReadOnlyField label="dev server 监听" value={`${devServer.host}:${devServer.port}`} />
        <ReadOnlyField label="adb 路径" value={adb.path} mono />
      </div>
      <p className="text-[11px] text-ink-muted">
        这些端口由部署运维通过编辑 <code>agent-local.json</code> + 重启调整。SPA 端不提供改写入口，避免一名用户改了端口让其他人掉线。
      </p>
    </Card>
  );
}

function ReadOnlyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-ink-muted">{label}</span>
      <input
        className={clsx('input', mono && 'font-mono text-[11px]')}
        value={value}
        readOnly
      />
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Diagnostics (shell snippets + missing-tools list)                  */
/* ----------------------------------------------------------------- */

export function DiagnosticsCard(): JSX.Element {
  const { data } = useQuery({
    queryKey: ['host-tools'],
    queryFn: () => hostToolsApi.get(),
    refetchOnWindowFocus: false,
  });
  if (!data) return <></>;
  return (
    <Card title="Shell 命令 · 诊断" icon={<Info className="h-4 w-4 text-accent" />}>
      <DiagnosticsBody data={data} />
    </Card>
  );
}

function DiagnosticsBody({ data }: { data: HostToolsResponse }): JSX.Element {
  const [shell, setShell] = useState<'zsh' | 'bash' | 'fish' | 'powershell' | 'cmd'>('zsh');
  const { data: snippets, isFetching } = useQuery({
    queryKey: [
      'host-tools-shell-snippets',
      shell,
      data.androidSdkRoot,
      data.tools.map((t) => t.resolvedPath).join('|'),
    ],
    queryFn: () => hostToolsApi.shellSnippets(shell),
  });
  const text = useMemo(() => snippets?.lines.join('\n') ?? '', [snippets]);
  const copyRef = useRef<HTMLPreElement | null>(null);
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      const sel = window.getSelection();
      if (copyRef.current && sel) {
        const range = document.createRange();
        range.selectNodeContents(copyRef.current);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };
  const missingTools = data.tools.filter((t) => !t.available && t.triedPaths.length > 0);
  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-ink-muted">
          {snippets?.doc ?? '复制下方命令到 shell rc 文件，hub 之外的终端也能用上同一份路径。'}
        </p>
        <div className="flex items-center gap-1.5">
          <select
            className="select text-xs"
            value={shell}
            onChange={(e) => setShell(e.target.value as typeof shell)}
          >
            <option value="zsh">zsh</option>
            <option value="bash">bash</option>
            <option value="fish">fish</option>
            <option value="powershell">PowerShell</option>
            <option value="cmd">cmd.exe</option>
          </select>
          <button type="button" className="btn-ghost btn-xs text-xs" onClick={onCopy} disabled={text.length === 0}>
            <Copy className="h-3 w-3" /> {copied ? '已复制' : '复制'}
          </button>
        </div>
      </div>
      <pre
        ref={copyRef}
        className="overflow-x-auto rounded-md border border-line bg-bg-soft p-2 font-mono text-[11px] leading-relaxed"
      >
        {isFetching && !text ? '正在生成…' : text || '# 当前没有任何已解析路径，先在上方表格里配置一个工具。'}
      </pre>
      {missingTools.length > 0 && (
        <details className="text-xs text-ink-muted">
          <summary className="cursor-pointer select-none">未找到的工具 · {missingTools.length} 个</summary>
          <ul className="mt-2 space-y-2">
            {missingTools.map((t) => (
              <li key={t.name} className="rounded-md border border-line bg-bg-soft px-2 py-1.5">
                <div className="flex items-center gap-1 font-mono text-[11.5px]">
                  <XCircle className="h-3 w-3 text-rose-500" /> {t.label}
                </div>
                <p className="mt-0.5 text-[10.5px] text-ink-dim">{t.description}</p>
                <details className="mt-1 text-[10.5px]">
                  <summary className="cursor-pointer select-none">已查找的 {t.triedPaths.length} 个候选</summary>
                  <ul className="mt-1 max-h-32 overflow-y-auto rounded-sm bg-bg-card p-1 font-mono">
                    {t.triedPaths.map((p) => (
                      <li key={p} className="break-all">
                        {p}
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

// Re-export the type so consumers that build custom UI on top of the
// host-tools shell snippets API can import it from the same module.
export type { HostToolsShellSnippets };
